import Vue from "vue";
import { store } from "@/store/store";
import Default from "../layouts/Default";
import PaymentLayout from "../layouts/Payment.vue";
import FreeToolLayout from "../layouts/FreeTool.vue";
import { Permission } from "@/enums/permission";
import { permissionCheck } from "@/helpers/permission-check";
import * as Sentry from "@sentry/vue";
import { Module } from "@/enums/modules";
import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import { i18n } from "../main";
import { getSubscriptionViewRedirect } from "@/helpers/subscription-view";
import {
  isFeatureFlagEnabled,
  isFeatureFlagEnabledAsync,
  FEATURE_FLAG
} from "@/helpers/posthog";

const Dashboard = () => import("../views/dashboard/Dashboard");

const VisibilityBusinessAddresses = () =>
  import("../views/visibility/BusinessAddresses");
const VisibilityWebsites = () => import("../views/visibility/Websites");

const CompetitiveBusinessAddresses = () =>
  import("../views/competition/BusinessAddresses");
const CompetitiveWebsites = () => import("../views/competition/Websites");
const CompetitionVisibility = () => import("../views/competition/Visibility");

const AnalyticsWebsites = () => import("../views/analytics/Websites");
const AnalyticsGMBAddresses = () => import("../views/analytics/GMBAddresses");
const AnalyticsGMBAddressesArchive = () =>
  import("../views/analytics/GMBAddressesArchive");
const AnalyticsPeriodic = () => import("../views/analytics/Periodic");
const AnalyticsPeriodicChart = () =>
  import("../views/analytics/tabs/PeriodicChart");
const AnalyticsPeriodicTable = () =>
  import("../views/analytics/tabs/PeriodicTable");

const BusinessAddresses = () =>
  import("../views/businessAddresses/BusinessAddresses");
const BusinessAddressesList = () =>
  import("../views/businessAddresses/BusinessAddressesList");
const BusinessAddressesGroups = () =>
  import("../views/businessAddresses/BusinessAddressesGroups");
const LocationsQuality = () =>
  import("../views/businessAddresses/LocationsQuality");

const ExternalChangesList = () =>
  import("../views/externalChanges/ExternalChangesList");
const ExternalChangesProtect = () =>
  import("../views/externalChanges/ExternalChangesProtect");
const ExternalChangesHistory = () =>
  import("../views/externalChanges/ExternalChangesHistory");

const Photos = () => import("../views/photos/Photos");
const UserPhotos = () => import("../views/photos/UserPhotos");
const AddPhoto = () => import("../views/photos/AddPhoto");

const PostsList = () => import("../views/posts/PostsList");
const PostsListPlanned = () => import("../views/posts/PostsListPlanned");
const PostsListSent = () => import("../views/posts/PostsListSent");
const PostsAddNew = () => import("../views/posts/PostsAddNew");
const PostsEditPlannedPost = () =>
  import("../views/posts/PostsEditPlannedPost");

const ReviewsList = () => import("../views/reviews/ReviewsList");
const ReviewsAnalyst = () => import("../views/reviews/ReviewsAnalyst");
const ReviewsCategories = () => import("../views/reviews/ReviewsCategories");
const ReviewsCategoriesAnalytics = () =>
  import("../views/reviewBooster/ReviewsCategoriesAnalytics");
const ReviewBoosterCategoriesDetails = () =>
  import("../views/reviews/ReviewsCategoryDetails.vue");
const ReviewsCategoryDetails = () =>
  import("../views/reviews/ReviewsCategoryDetails.vue");

const SearchKeywords = () =>
  import("../views/searchKeywords/SearchKeywords.vue");

const ProfileCompanyProfile = () =>
  import("../views/company/CompanyProfile.vue");
const ProfileListingsConfiguration = () =>
  import("../views/company/ListingsConfiguration.vue");

const ProfileMapSettings = () => import("../views/company/MapSettings.vue");

const ProfileWebsites = () => import("../views/company/Websites.vue");
const ProfileCompanyInWeb = () => import("../views/company/CompanyInWeb.vue");
const FacilityDetails = () => import("../views/company/FacilityDetails.vue");
const FacilityEditSelected = () =>
  import("../views/company/FacilityEditSelected.vue");

const FacilityDetailsTab = () => import("../views/company/tabs/Details.vue");
const FacilityExternalChangesListTab = () =>
  import("../views/company/tabs/ExternalChangesList.vue");
const FacilityExternalChangesHistoryTab = () =>
  import("../views/company/tabs/ExternalChangesHistory.vue");
const StoreLocatorConfigTab = () =>
  import("../views/company/tabs/StoreLocatorConfig.vue");
const StoreLocatorFaqTab = () =>
  import("../views/company/tabs/StoreLocatorFaq.vue");
const StoreLocatorTab = () => import("../views/company/tabs/StoreLocator.vue");

const FacilityCreate = () => import("../views/company/FacilityCreate");
const FacilityAddNew = () => import("../views/company/FacilityAddNew");

const Permissions = () => import("../views/permissions/Permissions");

const UserSettings = () => import("../views/user/Settings");
const UserSettingsUserProfile = () => import("../views/user/UserProfile");
const UserSettingsSecurity = () => import("../views/user/Security");
const UserSettingsConsentsAndRegulations = () =>
  import("../views/user/ConsentsAndRegulations");
const UserSettingsAccountSettings = () =>
  import("../views/user/LegacyAccountSettings");
const Subscription = () => import("../views/user/Subscription.vue");
const UserSettingsDateAndTime = () => import("../views/user/DateAndTime");

const StoreLocatorConfig = () =>
  import("../views/storeLocator/StoreLocatorConfig");
const StoreLocatorFaq = () => import("../views/storeLocator/StoreLocatorFaq");
const StoreLocator = () => import("../views/storeLocator/StoreLocator");

const ReviewBoosterConfig = () =>
  import("../views/reviewBooster/ReviewBoosterConfig");
const ReviewBoosterFormList = () => import("../views/reviewBooster/FormList");

const FacebookAccountsList = () => import("../views/facebookAccounts/List");
const AppleMapsAccountsList = () => import("../views/appleMaps/List");
const ReviewBoosterReviewsList = () =>
  import("../views/reviewBooster/ReviewsList");
const MobileAppAccountsList = () => import("../views/mobileAppAccounts/List");
const OrderDetails = () => import("../views/payment/OrderDetails.vue");
const InvoiceData = () => import("../views/payment/InvoiceData.vue");
const Checkout = () => import("../views/payment/Checkout.vue");
const Completed = () => import("../views/payment/Completed");
const QuestionsAndAnswers = () =>
  import("../views/questionsAndAnswers/QuestionsAndAnswers");

const CompetitionComparison = () =>
  import("../views/CompetitionComparison/CompetitionComparison");
const CompetitionComparisonOld = () =>
  import("../views/CompetitionComparison/CompetitionComparisonOld");
const CompetitionComparisonFreeTool = () =>
  import("../views/CompetitionComparison/CompetitionComparisonFreeTool");
const CompetitionComparisonEmailConfirmationFreeTool = () =>
  import(
    "../views/CompetitionComparison/CompetitionComparisonEmailConfirmationFreeTool"
  );
const AppsReviewsList = () => import("../views/appReviews/ReviewsList");
const MobileAppReviewsCategoriesAnalytics = () =>
  import("../views/mobileAppAccounts/ReviewsCategoriesAnalytics");
const MobileAppReviewsCategoryDetails = () =>
  import("../views/mobileAppAccounts/ReviewsCategoryDetails");

const ReviewBoosterStatistics = () =>
  import("../views/reviewBoosterStatistics/ReviewBoosterStatistics");

const hasAccess = (to, from, next) => {
  // Company-dependent routes must wait for the selected company's permissions.
  const company = store.state.company.company;
  if (!company) return next(false);
  const isSubscriptionPage = [
    "subscription-settings",
    "legacy-subscription-settings"
  ].includes(to.name);
  const isUnpaidSubscription = store.getters["company/isUnpaidSubscription"];
  const isFreeTrial = store.getters["auth/isFreeTrial"];
  const isSubscriptionPlanClient =
    store.getters["company/isSubscriptionPlanClient"];
  const hasSelfServiceBilling = store.getters["company/hasSelfServiceBilling"];
  const isPaymentFlowNotStarted =
    company.initial_payment_status === "not_started";
  if (hasSelfServiceBilling && isPaymentFlowNotStarted && !isFreeTrial && isSubscriptionPlanClient) {
    const data = {
      text: i18n.t("message.service.subscriptionPlanExpired"),
      path: "subscription-settings",
      buttonText: i18n.t("message.common.settings")
    };
    if (!isSubscriptionPage && !("isPaymentFlow" in to.meta)) {
      store.dispatch("settings/openModal", data);
    }
  } else if (
    hasSelfServiceBilling &&
    isUnpaidSubscription &&
    !isSubscriptionPage &&
    !("isPaymentFlow" in to.meta) &&
    !isFreeTrial
  ) {
    Vue.notify({
      group: "default",
      type: "error",
      text: i18n.t("message.payments.completeYourPayment")
    });
    return next({ name: "order-details" });
  }

  if (to.meta.companyFlagCheck) {
    if (!store.getters[`company/${to.meta.companyFlagCheck}`]) {
      next({
        name: "dashboard"
      });
    }
  }

  if (to.meta.featureFlag) {
    if (!isFeatureFlagEnabled(to.meta.featureFlag)) {
      next({
        name: "dashboard"
      });
    }
  }

  const getPermissionStatus = () => {
    return to.meta && to.meta.permission
      ? permissionCheck(to.meta.permission)
      : true;
  };

  const getPlanRestrictionStatus = () => {
    return to.meta && to.meta.planRestrictions
      ? planRestrictionsCheck(to.meta.planRestrictions)
      : true;
  };

  if (getPermissionStatus()) {
    if (getPlanRestrictionStatus()) {
      next();
    } else {
      next({
        name: "dashboard"
      });
    }
  } else {
    next({
      name: "dashboard"
    });
  }
};

const subscriptionViewAccess = (to, from, next) => {
  return hasAccess(to, from, target => {
    if (target !== undefined) return next(target);
    const redirect = getSubscriptionViewRedirect(
      to,
      store.getters["settings/stripeSubscriptionUiEnabled"],
      store.getters["company/hasSelfServiceBilling"]
    );
    return redirect ? next(redirect) : next();
  });
};

const canAccessFreeTool = (to, from, next) => {
  if (to.meta.featureFlag) {
    if (!isFeatureFlagEnabled(to.meta.featureFlag)) {
      next({
        name: "dashboard"
      });
    } else {
      next();
    }
  }
};

export default [
  {
    path: "/",
    component: Default,
    redirect: "/dashboard",
    children: [
      {
        path: "/app-reviews/reviews",
        name: "app-reviews-reviews",
        component: AppsReviewsList,
        beforeEnter: hasAccess,
        meta: {
          group: "app-reviews",
          analyticsEventId: "/app-reviews/reviews",
          requiresAuth: true,
          module: Module.APP_REVIEWS,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_USE_MOBILE_PLATFORMS_INTEGRATION
        }
      },
      {
        path: "/app-reviews/categories-analytics",
        name: "app-reviews-categories-analytics",
        component: MobileAppReviewsCategoriesAnalytics,
        beforeEnter: hasAccess,
        meta: {
          group: "app-reviews",
          analyticsEventId: "/app-reviews/categories-analytics",
          requiresAuth: true,
          module: Module.APP_REVIEWS,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_USE_MOBILE_PLATFORMS_INTEGRATION
        }
      },
      {
        path: "/app-reviews/category",
        name: "app-reviews-category-details",
        component: MobileAppReviewsCategoryDetails,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/app-reviews/category",
          group: "app-reviews",
          requiresAuth: true,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          module: Module.REVIEWS_CATEGORIES_ANALYTICS,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_USE_MOBILE_PLATFORMS_INTEGRATION
        }
      },
      {
        path: "/dashboard",
        name: "dashboard",
        component: Dashboard,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/dashboard",
          group: "dashboard",
          title: "message.common.dashboard",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.VIEW_DASHBOARD
        }
      },
      {
        path: "/monitoring/single-location",
        name: "visibility-business-addresses",
        component: VisibilityBusinessAddresses,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/visibility/business-addresses",
          group: "monitoring",
          title: "message.common.yourVisibility",
          breadcrumb: null,
          requiresAuth: true,
          module: Module.POSITION_SINGLE_LOCATION,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_VIEW_SEO_LOCAL_SEARCH_POSITIONS
        }
      },
      {
        path: "/visibility/websites",
        name: "visibility-websites",
        component: VisibilityWebsites,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/visibility/websites",
          group: "visibility",
          title: "message.common.yourVisibility",
          breadcrumb: null,
          requiresAuth: true
        }
      },
      {
        path: "/company/competition-locations",
        name: "competition-business-addresses",
        component: CompetitiveBusinessAddresses,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/competition/business-addresses",
          group: "company",
          title: "message.common.competitiveBusinessAddresses",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.MANAGE_COMPETITION_PLACES,
          module: Module.SETTINGS_COMPETITION_LOCATIONS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_COMPARE_COMPETITION
        }
      },
      {
        path: "/competition/websites",
        name: "competition-websites",
        component: CompetitiveWebsites,
        meta: {
          analyticsEventId: "/competition/websites",
          group: "competition",
          title: "message.common.competitiveWebsites",
          breadcrumb: null,
          requiresAuth: true
        }
      },
      {
        path: "/monitoring/competition-visibility",
        name: "competition-visibility",
        component: CompetitionVisibility,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/competition/visibility",
          group: "monitoring",
          title: "message.common.competitionVisibility",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.VIEW_MAPS_COMPETITION_RANKINGS,
          module: Module.POSITION_COMPETITION_COMPARISON,
          planRestrictions: PLAN_RESTRICTIONS.CAN_COMPARE_COMPETITION
        }
      },
      {
        path: "/analytics/websites",
        name: "analytics-websites",
        component: AnalyticsWebsites,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/analytics/websites",
          group: "analytics",
          title: "message.common.www_analytics",
          breadcrumb: null,
          requiresAuth: true
        }
      },
      {
        path: "/users-behavior",
        name: "analytics-gmb-addresses",
        component: AnalyticsGMBAddresses,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/analytics/gmb-addresses",
          group: "users-behavior",
          title: "message.common.analytics",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.VIEW_GMB_INSIGHTS,
          module: Module.USERS_BEHAVIOR,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_MONITOR_GMB_ACTIONS_AND_REPORTS
        }
      },
      {
        path: "/users-behavior-archive",
        name: "analytics-gmb-addresses-archive",
        component: AnalyticsGMBAddressesArchive,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/analytics/gmb-addresses/archive",
          group: "users-behavior",
          title: "message.common.analytics",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.VIEW_GMB_INSIGHTS,
          module: Module.USERS_BEHAVIOR
        }
      },
      {
        path: "/monitoring/all-locations",
        name: "analytics-search-ranking",
        redirect: "/monitoring/all-locations/table",
        component: AnalyticsPeriodic,
        meta: {
          analyticsEventId: "/analytics/search-ranking",
          group: "monitoring",
          title: "message.common.analytics",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.VIEW_GMB_INSIGHTS,
          module: Module.POSITION_ALL_LOCATIONS,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_VIEW_SEO_LOCAL_SEARCH_POSITIONS
        },
        children: [
          {
            path: "/monitoring/all-locations/chart",
            name: "analytics-search-ranking-chart",
            component: AnalyticsPeriodicChart,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId: "/analytics/search-ranking/chart",
              group: "monitoring",
              position: 1,
              requiresAuth: true,
              permission: Permission.VIEW_GMB_INSIGHTS,
              module: Module.POSITION_ALL_LOCATIONS
            }
          },
          {
            path: "/monitoring/all-locations/table",
            name: "analytics-search-ranking-table",
            component: AnalyticsPeriodicTable,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId: "/analytics/search-ranking/table",
              group: "monitoring",
              position: 2,
              requiresAuth: true,
              permission: Permission.VIEW_GMB_INSIGHTS,
              module: Module.POSITION_ALL_LOCATIONS
            }
          }
        ]
      },

      // Business-addresses
      {
        path: "/business-addresses",
        name: "business-addresses",
        redirect: "/business-addresses/list",
        component: BusinessAddresses,
        meta: {
          analyticsEventId: "/business-addresses",
          title: "message.common.settings",
          breadcrumb: null,
          requiresAuth: true
        },
        children: [
          {
            path: "/business-addresses/list",
            name: "business-addresses-list",
            component: BusinessAddressesList,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId: "/business-addresses/list",
              group: "business-addresses",
              position: 1,
              requiresAuth: true,
              permission: Permission.VIEW_FACILITIES_DATA,
              module: Module.LOCATIONS_MANAGE
            }
          },
          {
            path: "/business-addresses/groups",
            name: "business-addresses-groups",
            component: BusinessAddressesGroups,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId: "/business-addresses/groups",
              group: "business-addresses",
              position: 2,
              requiresAuth: true,
              permission: Permission.VIEW_FACILITIES_DATA
            }
          }
        ]
      },
      {
        path: "/business-addresses/quality",
        name: "locations-quality",
        component: LocationsQuality,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/business-addresses/quality",
          group: "business-addresses",
          requiresAuth: true,
          permission: Permission.VIEW_FACILITIES_DATA,
          module: Module.LOCATIONS_QUALITY,
          featureFlag: FEATURE_FLAG.CN_6145_FACILITIES_QUALITY
        }
      },
      {
        path: "/business-addresses/create",
        name: "facility-create",
        component: FacilityCreate,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/business-addresses/create",
          group: "business-addresses",
          requiresAuth: true,
          permission: Permission.VIEW_FACILITIES_DATA,
          companyFlagCheck: "canCreateNewFacility"
        }
      },
      {
        path: "/business-addresses/add/new",
        name: "profile-facility-add-new",
        component: FacilityAddNew,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/business-addresses/add/new",
          group: "business-addresses",
          requiresAuth: true,
          permission: Permission.VIEW_FACILITIES_DATA
        }
      },
      {
        path: "/business-addresses/edit/selected",
        name: "profile-facility-edit-selected",
        component: FacilityEditSelected,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/business-addresses/edit/selected",
          group: "business-addresses",
          requiresAuth: true,
          permission: Permission.VIEW_FACILITIES_DATA
        }
      },
      {
        path: "/business-addresses/:id",
        name: "profile-facility-details",
        component: FacilityDetails,
        beforeEnter: hasAccess,
        redirect: "/business-addresses/:id/details",
        meta: {
          analyticsEventId: "/business-addresses/:id",
          group: "business-addresses",
          requiresAuth: true,
          permission: Permission.VIEW_FACILITIES_DATA
        },
        children: [
          {
            path: "/business-addresses/:id/details",
            name: "facility-details",
            component: FacilityDetailsTab,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId: "/business-addresses/:id/details",
              group: "business-addresses",
              position: 1,
              requiresAuth: true,
              permission: Permission.VIEW_FACILITIES_DATA
            }
          },
          {
            path: "/business-addresses/:id/external-changes-list",
            name: "facility-external-changes-list",
            component: FacilityExternalChangesListTab,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId: "/business-addresses/:id/external-changes-list",
              group: "business-addresses",
              position: 2,
              requiresAuth: true,
              permission: Permission.VIEW_EXTERNAL_CHANGES
            }
          },
          {
            path: "/business-addresses/:id/external-changes-history",
            name: "facility-external-changes-history",
            component: FacilityExternalChangesHistoryTab,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId:
                "/business-addresses/:id/external-changes-history",
              group: "business-addresses",
              position: 3,
              requiresAuth: true,
              permission: Permission.VIEW_EXTERNAL_CHANGES_HISTORY
            }
          },
          {
            path: "/business-addresses/:id/store-locator",
            name: "store-locator-config",
            component: StoreLocatorTab,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId: "/business-addresses/:id/store-locator",
              group: "business-addresses",
              position: 3,
              requiresAuth: true
            }
          }
        ]
      },
      {
        path: "/business-addresses/:id/store-locator-config",
        name: "store-locator-config-manage",
        component: StoreLocatorConfigTab,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/business-addresses/:id/store-locator-config",
          group: "business-addresses",
          requiresAuth: true
        }
      },
      {
        path: "/business-addresses/:id/store-locator-faq",
        name: "store-locator-tab-faq",
        component: StoreLocatorFaqTab,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/business-addresses/:id/store-locator-faq",
          group: "business-addresses",
          requiresAuth: true
        }
      },

      // External changes
      {
        path: "/external-changes",
        name: "external-changes",
        redirect: "/external-changes/list",
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/external-changes",
          title: "message.externalChanges.title",
          breadcrumb: null,
          requiresAuth: true,
          module: Module.EXTERNAL_CHANGES_LIST,
          planRestrictions: PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
        }
      },
      {
        path: "/external-changes/list",
        name: "external-changes-list",
        component: ExternalChangesList,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/external-changes/list",
          group: "external-changes",
          position: 1,
          requiresAuth: true,
          permission: Permission.VIEW_EXTERNAL_CHANGES,
          module: Module.EXTERNAL_CHANGES_LIST,
          planRestrictions: PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
        }
      },
      {
        path: "/external-changes/protect",
        name: "external-changes-protect",
        component: ExternalChangesProtect,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/external-changes/protect",
          group: "external-changes",
          requiresAuth: true,
          permission: Permission.PROTECT_FROM_EXTERNAL_CHANGES,
          module: Module.EXTERNAL_CHANGES_PROTECT,
          planRestrictions: PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
        }
      },
      {
        path: "/external-changes/history",
        name: "external-changes-history",
        component: ExternalChangesHistory,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/external-changes/history",
          group: "external-changes",
          requiresAuth: true,
          permission: Permission.VIEW_EXTERNAL_CHANGES_HISTORY,
          module: Module.EXTERNAL_CHANGES_HISTORY,
          planRestrictions: PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
        }
      },

      // Photos
      {
        path: "/photos",
        name: "photos",
        component: Photos,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/photos",
          group: "photos",
          title: "message.common.photos",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.VIEW_PHOTOS,
          module: Module.PHOTOS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_POST_PHOTOS
        }
      },

      {
        path: "/photos/users",
        name: "photos-users",
        component: UserPhotos,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/photos/users",
          group: "photos",
          title: "message.photos.customer.title",
          breadcrumb: null,
          requiresAuth: true,
          featureFlag: FEATURE_FLAG.CEN_760_SHOW_CUSTOMERS_MEDIA,
          permission: Permission.VIEW_AND_REPORT_CUSTOMER_MEDIA,
          module: Module.PHOTOS
        }
      },

      {
        path: "/photos/add",
        name: "photos-add",
        component: AddPhoto,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/photos/add",
          group: "photos",
          position: 2,
          requiresAuth: true,
          permission: Permission.ADD_AND_DELETE_PHOTOS,
          module: Module.PHOTOS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_POST_PHOTOS
        }
      },

      // Posts
      {
        path: "/posts",
        name: "posts",
        redirect: "/posts/list/sent",
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/posts",
          title: "message.common.posts",
          breadcrumb: null,
          requiresAuth: true,
          module: Module.POSTS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_PUBLISH_MARKETING_PUBLICATIONS
        }
      },
      {
        path: "/posts/list",
        name: "posts-list",
        redirect: "/posts/list/sent",
        component: PostsList,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/posts/list",
          group: "posts",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.VIEW_LOCAL_POSTS,
          module: Module.POSTS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_PUBLISH_MARKETING_PUBLICATIONS
        },
        children: [
          {
            path: "/posts/list/sent",
            name: "posts-list-sent",
            component: PostsListSent,
            meta: {
              analyticsEventId: "/posts/list/sent",
              position: 1,
              requiresAuth: true,
              planRestrictions:
                PLAN_RESTRICTIONS.CAN_PUBLISH_MARKETING_PUBLICATIONS
            }
          },
          {
            path: "planned",
            name: "posts-list-planned",
            component: PostsListPlanned,
            meta: {
              analyticsEventId: "/posts/list/planned",
              position: 2,
              requiresAuth: true,
              planRestrictions:
                PLAN_RESTRICTIONS.CAN_PUBLISH_MARKETING_PUBLICATIONS
            }
          }
        ]
      },
      {
        path: "/posts/add",
        name: "posts-add",
        component: PostsAddNew,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/posts/add",
          group: "posts",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.ADD_AND_DELETE_LOCAL_POSTS,
          module: Module.POSTS
        }
      },
      {
        path: "/posts/scheduled/:id/edit",
        name: "posts-scheduled-edit",
        component: PostsEditPlannedPost,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/posts/scheduled/:id/edit",
          group: "posts",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.ADD_AND_DELETE_LOCAL_POSTS,
          module: Module.POSTS
        }
      },
      // Reviews
      {
        path: "/reviews",
        name: "reviews",
        redirect: "/reviews/list",
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/reviews",
          title: "message.reviews.reviewsList",
          breadcrumb: null,
          requiresAuth: true,
          module: Module.REVIEWS
        }
      },
      {
        path: "/reviews/list",
        name: "reviews-list",
        component: ReviewsList,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/reviews/list",
          group: "reviews",
          position: 1,
          requiresAuth: true,
          permission: Permission.VIEW_REVIEWS,
          module: Module.REVIEWS_LIST,
          planRestrictions: PLAN_RESTRICTIONS.CAN_MONITOR_RATINGS_AND_REPORTS
        }
      },
      {
        path: "/reviews/analyst",
        name: "reviews-analyst",
        component: ReviewsAnalyst,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/reviews/analyst",
          group: "reviews",
          requiresAuth: true,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          module: Module.REVIEWS_ANALYTICS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_MONITOR_RATINGS_AND_REPORTS
        }
      },
      {
        path: "/reviews/categories",
        name: "reviews-categories",
        component: ReviewsCategories,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/reviews/categories",
          group: "reviews",
          requiresAuth: true,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          module: Module.REVIEWS_CATEGORIES_ANALYTICS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_MANUALLY_ADD_SENTIMENT
        }
      },

      {
        path: "/reviews/category/",
        name: "reviews-category-details",
        component: ReviewsCategoryDetails,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/reviews/categories/details",
          group: "reviews",
          requiresAuth: true,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          module: Module.REVIEWS_CATEGORIES_ANALYTICS
        }
      },
      {
        path: "/reviews/competition-comparison-old",
        name: "reviews-competition-comparison",
        component: CompetitionComparisonOld,
        meta: {
          group: "reviews",
          requiresAuth: true,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          module: Module.REVIEWS_CATEGORIES_ANALYTICS,
          featureFlag: FEATURE_FLAG.CEN_37_COMPETITOR_REVIEW_ANALYSIS
        },
        beforeEnter: hasAccess
      },
      {
        path: "/reviews/competition-comparison",
        name: "reviews-competition-comparison-v2",
        component: CompetitionComparison,
        meta: {
          group: "reviews",
          requiresAuth: true,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          module: Module.REVIEWS_CATEGORIES_ANALYTICS,
          featureFlag: FEATURE_FLAG.CEN_37_COMPETITOR_REVIEW_ANALYSIS
        },
        beforeEnter: hasAccess
      },

      // Search Keywords
      {
        path: "/search-keywords",
        name: "search-keywords",
        component: SearchKeywords,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/search-keywords",
          group: "search-keywords",
          requiresAuth: true,
          permission: Permission.VIEW_GMB_INSIGHTS,
          module: Module.SEARCH_KEYWORDS,
          featureFlag: FEATURE_FLAG.CEN_394_KEYWORDS_IMPRESSIONS_ANALYTICS
        }
      },

      // Company
      {
        path: "/company",
        name: "company-profile",
        redirect: "/company/company-profile",
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/company",
          group: "company",
          title: "message.common.companyProfile",
          breadcrumb: null,
          requiresAuth: true,
          permission: Permission.VIEW_COMPANY_DATA,
          module: Module.SETTINGS
        }
      },
      {
        path: "/company/company-profile",
        name: "profile-company-profile",
        component: ProfileCompanyProfile,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/company/company-profile",
          group: "company",
          requiresAuth: true,
          permission: Permission.VIEW_COMPANY_DATA,
          module: Module.SETTINGS_COMPANY
        }
      },
      {
        path: "/company/websites",
        name: "profile-websites",
        component: ProfileWebsites,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/company/websites",
          group: "company",
          requiresAuth: true
        }
      },
      {
        path: "/company/company-in-web",
        name: "profile-company-in-web",
        component: ProfileCompanyInWeb,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/company/company-in-web",
          group: "company",
          requiresAuth: true,
          permission: Permission.MANAGE_KEYWORDS,
          module: Module.SETTINGS_KEYWORDS,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_VIEW_SEO_LOCAL_SEARCH_POSITIONS
        }
      },
      {
        path: "/company/gmb-accounts",
        name: "profile-gmb-accounts",
        component: ProfileListingsConfiguration,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/company/gmb-accounts",
          group: "company",
          requiresAuth: true,
          permission: Permission.MANAGE_GMB_ACCOUNT,
          module: Module.SETTINGS_GMB_ACCOUNTS
        }
      },
      {
        path: "/company/facebook-accounts",
        name: "profile-facebook-accounts",
        component: FacebookAccountsList,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/company/facebook-accounts",
          group: "company",
          requiresAuth: true,
          module: Module.SETTINGS_FACEBOOK_ACCOUNTS
        }
      },
      {
        path: "/company/apple-accounts",
        name: "profile-apple-accounts",
        component: AppleMapsAccountsList,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/company/apple-maps",
          group: "company",
          requiresAuth: true,
          module: Module.SETTINGS_MAP_CONFIG
        }
      },
      {
        path: "/company/map-settings",
        name: "profile-map-settings",
        component: ProfileMapSettings,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/company/map-settings",
          group: "company",
          requiresAuth: true,
          permission: Permission.MANAGE_GMB_ACCOUNT,
          module: Module.SETTINGS_MAP_CONFIG
        }
      },
      {
        path: "/company/mobile-apps",
        name: "profile-mobile-apps",
        component: MobileAppAccountsList,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/company/mobile-apps",
          group: "company",
          requiresAuth: true,
          module: Module.SETTINGS_APPS_CONFIG,
          permission: Permission.MANAGE_GOOGLE_PLAY_ACCOUNT,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_USE_MOBILE_PLATFORMS_INTEGRATION
        }
      },

      // Permissions
      {
        path: "/permissions",
        name: "permissions",
        component: Permissions,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/permissions",
          requiresAuth: true,
          permission: Permission.VIEW_TEAM,
          module: Module.TEAM
        }
      },

      {
        path: "/subscription",
        name: "subscription-settings",
        component: Subscription,
        beforeEnter: subscriptionViewAccess,
        meta: {
          analyticsEventId: "/subscription",
          requiresAuth: true,
          permission: Permission.MANAGE_COMPANY_DATA
        }
      },

      // User-settings
      {
        path: "/user-settings",
        name: "user-settings",
        redirect: "/user-settings/user-profile",
        component: UserSettings,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/user-settings",
          title: "message.common.settings",
          breadcrumb: null,
          requiresAuth: true
        },
        children: [
          {
            path: "/user-settings/user-profile",
            name: "user-settings-user-profile",
            component: UserSettingsUserProfile,
            meta: {
              analyticsEventId: "/user-settings/user-profile",
              position: 1,
              requiresAuth: true
            }
          },
          {
            path: "/user-settings/security",
            name: "user-settings-security",
            component: UserSettingsSecurity,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId: "/user-settings/security",
              position: 2,
              requiresAuth: true,
              featureFlag: FEATURE_FLAG.CEN_847_TWO_FACTOR_AUTHENTICATION
            }
          },
          {
            path: "/user-settings/consents-and-regulations",
            name: "user-settings-consents-and-regulations",
            component: UserSettingsConsentsAndRegulations,
            meta: {
              analyticsEventId: "/user-settings/consents-and-regulations",
              position: 3,
              requiresAuth: true
            }
          },
          {
            path: "/user-settings/account-settings",
            name: "legacy-subscription-settings",
            component: UserSettingsAccountSettings,
            beforeEnter: subscriptionViewAccess,
            meta: {
              analyticsEventId: "/user-settings/account-settings",
              position: 4,
              requiresAuth: true,
              permission: Permission.MANAGE_COMPANY_DATA
            }
          },
          {
            path: "/user-settings/date-and-time",
            name: "date-and-time-settings",
            component: UserSettingsDateAndTime,
            beforeEnter: hasAccess,
            meta: {
              analyticsEventId: "/user-settings/date-and-time",
              position: 5,
              requiresAuth: true,
              featureFlag: FEATURE_FLAG.CN_7152_TIMEZONE_SETTINGS
            }
          }
        ]
      },

      // Store Locator
      {
        path: "/store-locator",
        name: "store-locator",
        redirect: "/store-locator/general-config",
        component: StoreLocator,
        beforeEnter: hasAccess,
        meta: {
          requiresAuth: true,
          module: Module.STORE_LOCATOR,
          permission: Permission.MANAGE_STORE_LOCATOR
        },
        children: [
          {
            path: "/store-locator/general-config",
            name: "store-locator-general-config",
            component: StoreLocatorConfig,
            meta: {
              analyticsEventId: "/store-locator/general-config",
              requiresAuth: true,
              module: Module.STORE_LOCATOR,
              permission: Permission.MANAGE_STORE_LOCATOR
            }
          },
          {
            path: "/store-locator/faq",
            name: "store-locator-faq",
            component: StoreLocatorFaq,
            meta: {
              analyticsEventId: "/store-locator/faq",
              requiresAuth: true,
              module: Module.STORE_LOCATOR,
              permission: Permission.MANAGE_STORE_LOCATOR
            }
          }
        ]
      },
      //Review Booster
      {
        path: "/review-booster/promo",
        name: "review-booster",
        component: ReviewBoosterConfig,
        beforeEnter: hasAccess,
        meta: {
          group: "review-booster",
          analyticsEventId: "/review-booster",
          requiresAuth: true,
          module: Module.REVIEW_BOOSTER
        }
      },
      {
        path: "/review-booster/form-list",
        name: "review-booster-form-list",
        component: ReviewBoosterFormList,
        beforeEnter: hasAccess,
        meta: {
          group: "review-booster",
          requiresAuth: true,
          analyticsEventId: "/review-booster/form-list"
        }
      },
      {
        path: "/review-booster/reviews",
        name: "review-booster-reviews",
        component: ReviewBoosterReviewsList,
        beforeEnter: hasAccess,
        meta: {
          group: "review-booster",
          analyticsEventId: "/review-booster/reviews",
          requiresAuth: true,
          module: Module.REVIEW_BOOSTER
        }
      },
      {
        path: "/review-booster/categories-analytics",
        name: "review-booster-categories-analytics",
        component: ReviewsCategoriesAnalytics,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/review-booster/categories-analytics",
          group: "review-booster",
          requiresAuth: true,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          module: Module.REVIEWS_CATEGORIES_ANALYTICS
        }
      },
      {
        path: "/review-booster/category",
        name: "review-booster-categories-details",
        component: ReviewBoosterCategoriesDetails,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/review-booster/category",
          group: "review-booster",
          requiresAuth: true,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          module: Module.REVIEWS_CATEGORIES_ANALYTICS
        }
      },
      {
        path: "/review-booster/statistics",
        name: "review-booster-statistics",
        component: ReviewBoosterStatistics,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/review-booster/statistics",
          group: "review-booster",
          requiresAuth: true,
          module: Module.REVIEW_BOOSTER,
          featureFlag: FEATURE_FLAG.REVIEW_BOOSTER_STATISTICS
        }
      },
      {
        path: "/questions-and-answers",
        name: "questions-and-answers",
        component: QuestionsAndAnswers,
        beforeEnter: hasAccess,
        meta: {
          requiresAuth: true,
          module: Module.QUESTIONS_AND_ANSWERS,
          featureFlag: FEATURE_FLAG.CN_6543_QUESTIONS_AND_ANSWERS
        }
      }
    ]
  },
  {
    path: "/order-details",
    component: PaymentLayout,
    children: [
      {
        path: "/order-details",
        name: "order-details",
        component: OrderDetails,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/order-details",
          title: "message.payment.title",
          breadcrumb: null,
          requiresAuth: true,
          isPaymentFlow: true
        }
      },
      {
        path: "/invoice-data",
        name: "invoice-data",
        component: InvoiceData,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/invoice-data",
          title: "message.payment.title",
          breadcrumb: null,
          requiresAuth: true,
          isPaymentFlow: true
        }
      },
      {
        path: "/checkout",
        name: "checkout",
        component: Checkout,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/checkout",
          title: "message.payment.title",
          breadcrumb: null,
          requiresAuth: true,
          isPaymentFlow: true
        }
      },
      {
        path: "/payment-completed",
        name: "payment-completed",
        component: Completed,
        beforeEnter: hasAccess,
        meta: {
          analyticsEventId: "/payment-completed",
          group: "payment-completed",
          title: "message.common.dashboard",
          breadcrumb: null,
          requiresAuth: true,
          isPaymentFlow: true
        }
      }
    ]
  },

  {
    path: "/competition-comparison",
    component: FreeToolLayout,
    children: [
      {
        path: ":lang/email-confirmation/:token",
        name: "competition-comparison-free-tool-email-confirmation",
        component: CompetitionComparisonEmailConfirmationFreeTool,
        meta: {
          requiresAuth: false,
          isFreeTool: true
        }
      },
      {
        path: ":lang/report/:reportId",
        name: "reviews-competition-comparison-free-tool-v2",
        component: CompetitionComparisonFreeTool,
        meta: {
          requiresAuth: false,
          isFreeTool: true
        }
      }
    ]
  }
];
